import { required, email, helpers, numeric, maxLength } from "@vuelidate/validators";
import { getContact, insertContact, updateContact } from "../services/contactLambda";
import { formatPhoneNumber, formatPostalCode, sanitizeNumber } from "@/utils/dataUtils";
import { contactEmailExists } from '@/services/RegistrationAPIService';
import { useAuthStore } from "@/stores/authStore";
import { MODEL_TYPES } from "@/utils/constants";
import { useFieldRequiredValidator } from "@/composables/FieldRequiredValidator";
import { debounce } from "lodash";
  
const emailCache = {};

const checkEmailDebounced = debounce(async (value, resolve) => {
    try {
        const duplicate = await contactEmailExists(value);
        emailCache[value] = !duplicate;
        resolve(!duplicate);
    } catch (error) {
        // On error, allow the value (fail open) or handle appropriately
        console.error('Error checking email:', error);
        resolve(true);
    }
}, 500);

const duplicateContactEmail = helpers.withAsync(async function(value, siblings) {
    if (!helpers.req(value) || !email.$validator(value)) {
        return true;
    }

    if (!siblings.isNew) return true;
    
    if (emailCache[value] !== undefined) {
        return emailCache[value];
    }

    return new Promise((resolve) => {
        checkEmailDebounced(value, resolve);
    });
});

const requiredFields = [
    { fieldName: 'firstName', modelType: MODEL_TYPES.Contact },
    { fieldName: 'lastName', modelType: MODEL_TYPES.Contact },
    { fieldName: 'address1', modelType: MODEL_TYPES.Contact },
    { fieldName: 'address2', modelType: MODEL_TYPES.Contact },
    { fieldName: 'city', modelType: MODEL_TYPES.Contact },
    { fieldName: 'stateProvince', modelType: MODEL_TYPES.Contact },
    { fieldName: 'postalCode', modelType: MODEL_TYPES.Contact },
    { fieldName: 'phone', modelType: MODEL_TYPES.Contact },
    { fieldName: 'email', modelType: MODEL_TYPES.Contact },
    { fieldName: 'countyId', modelType: MODEL_TYPES.Contact },
    { fieldName: 'countyName', modelType: MODEL_TYPES.Contact },
    { fieldName: 'otherCountyName', modelType: MODEL_TYPES.Contact },
    { fieldName: 'phoneExtension', modelType: MODEL_TYPES.Contact },
    { fieldName: 'fax', modelType: MODEL_TYPES.Contact },
    { fieldName: 'title', modelType: MODEL_TYPES.Contact },
];

const validators = useFieldRequiredValidator(requiredFields);
const requiredValidators = validators.requiredValidators();

/**
 * Represents a contact model with validation and data manipulation methods.
 */
export class ContactModel {
    /**
     * Creates an instance of ContactModel.
     * @param {boolean} isHceContact - Indicates if the contact is an HCE contact.
     */
    constructor(isHceContact) {
        this.contactId = -1;
        this.firstName = '';
        this.lastName = '';
        this.address1 = '';
        this.address2 = '';
        this._city = '';
        this.stateProvince = '';
        this.countyId = null;
        this.countyName = '';
        this._otherCountyName = '';
        this.postalCode = '';
        this.phone = '';
        this.phoneExtension = '';
        this.fax = '';
        this.email = '';
        this.fullAddress = '';
        this.primaryOrganizationId = null;
        this.primaryOrganizationName = '';
        this.organizationTypeDesc = '';
        this.isActive = true;
        this.isPortalUser = false;
        this.isPendingApproval = false;
        this.isNew = true;
        this.isHceContact = isHceContact;
        this.isInternalUser = false;
        this.title = '';
        this.allowEdit = true;
    }

    /**
     * Gets the city name.
     * @returns {string} The city name.
     */
    get city() {
        return this._city;
    }

    /**
     * Sets the city name and replaces "SAINT" with "ST.".
     * @param {string} value - The city name.
     */
    set city(value) {
        this._city = this.replaceSaintWithSt(value?.toUpperCase());
    }

    /**
     * Gets the active state as a string.
     * @returns {string} "Active" if active, otherwise "Inactive".
     */
    get activeState() {
        return this.isActive ? 'Active' : 'Inactive';
    }

    /**
     * Sets the data for the contact model.
     * @param {Object} data - The data to set.
     */
    setData(data) {
        this.contactId = data.contactId;
        this.firstName = data.firstName;
        this.lastName = data.lastName;
        this.address1 = data.address1;
        this.address2 = data.address2;
        this.city = data.city;
        this.stateProvince = data.stateProvince;
        this.countyId = data.countyId;
        this.countyName = data.countyName;
        this.otherCountyName = data.otherCountyName;
        this.postalCode = formatPostalCode(data.postalCode);
        this.phone = formatPhoneNumber(data.phone);
        this.fullAddress = `${this.address1}${this.address2 ? '<br>' + this.address2 : ''}<br>${this.city}, ${this.stateProvince} ${this.postalCode}`;
        this.phoneExtension = data.phoneExtension;
        this.fax = data.fax;
        this.email = data.email;
        this.primaryOrganizationId = data.primaryOrganizationId;
        this.primaryOrganizationName = data.primaryOrganizationName;
        this.organizationTypeDesc = data.organizationTypeDescription;
        this.isActive = data.isActive;
        this.isPortalUser = data.isPortalUser;
        this.isPendingApproval = data.isPendingApproval;
        this.isNew = false;
        this.isInternalUser = false;
        this.title = data.title;
    }

    /**
     * Gets the other county name.
     * @returns {string} The other county name.
     */
    get otherCountyName() {
        return this._otherCountyName;
    }

    /**
     * Sets the other county name in uppercase.
     * @param {string} value - The other county name.
     */
    set otherCountyName(value) {
        this._otherCountyName = value?.toUpperCase();
    }

    /**
     * Updates masked fields (phone, fax, postal code) by sanitizing their values.
     */
    updateMaskedFields() {
        if (this.phone) {
            this.phone = sanitizeNumber(this.phone);
        }

        if (this.fax) {
            this.fax = sanitizeNumber(this.fax);
        }

        if (this.postalCode) {
            this.postalCode = sanitizeNumber(this.postalCode);
        }
    }

    /**
     * Gets the validation rules for the contact model.
     * @returns {Object} The validation rules.
     */
    static getValidationRules() {
        const valueRequiredMsg = 'Value is required';
        return {
            contactId: '',
            primaryOrganizationId: { required },
            firstName: { required: helpers.withMessage(valueRequiredMsg, requiredValidators['firstName']), maxLength: maxLength(50) },
            lastName: { required: helpers.withMessage(valueRequiredMsg, requiredValidators['lastName']), maxLength: maxLength(50) },
            address1: { required: helpers.withMessage(valueRequiredMsg, requiredValidators['address1']), maxLength: maxLength(120) },
            address2: {required: helpers.withMessage(valueRequiredMsg, requiredValidators['address2']), maxLength: maxLength(120) },
            city: { required: helpers.withMessage(valueRequiredMsg, requiredValidators['city']), maxLength: maxLength(50) },
            stateProvince: { required: helpers.withMessage(valueRequiredMsg, requiredValidators['stateProvince']) },
            countyId: { required: helpers.withMessage(valueRequiredMsg, requiredValidators['countyId']) },
            countyName: '',
            otherCountyName: { maxLength: maxLength(120) },
            postalCode: { required: helpers.withMessage(valueRequiredMsg, requiredValidators['postalCode'])},
            phone: { required: helpers.withMessage(valueRequiredMsg, requiredValidators['phone']) },
            phoneExtension: { numeric },
            fax: { required: helpers.withMessage(valueRequiredMsg, requiredValidators['fax'])},
            email: { required: helpers.withMessage(valueRequiredMsg, requiredValidators['email']), email, duplicateContactEmail: helpers.withMessage('Email address already exists', duplicateContactEmail), maxLength: maxLength(254) },
            isPortalUser: { required },
            isActive: { required },
            isInternalUser: { required },
            isNew: '',
            title: { required: helpers.withMessage(valueRequiredMsg, requiredValidators['title']), maxLength: maxLength(120) }
        };
    }

    /**
     * Saves the contact model. Creates or updates the contact.
     * @returns {Promise<boolean>} True if the save was successful, otherwise false.
     */
    async save() {
        // Remove the mask from the phone and postal code fields
        this.updateMaskedFields();
        if (this.contactId === -1) {
            // Create the contact (user) in keycloak
            if (await insertContact(this)) {
                this.isNew = false;
                return true;
            }

            return false;
        } else {
            const store = useAuthStore();
            const selfOrganizationUpdate = this.contactId == store.user.contactId && this.primaryOrganizationId != store.user.primaryOrganizationId;
            await updateContact(this);

            if (selfOrganizationUpdate) {
                await store.refreshUserProfile();
            }
        }
    }

    /**
     * Fetches contact data by contact ID and sets it to the model.
     * @param {number} contactId - The ID of the contact to fetch.
     */
    async fetch(contactId) {
        const contact = await getContact(contactId);
        this.setData(contact);
    }

    /**
     * Fetches a contact by ID and returns a new ContactModel instance.
     * @param {number} contactId - The ID of the contact to fetch.
     * @returns {Promise<ContactModel>} The fetched contact model.
     */
    static async getContact(contactId) {
        const contact = new ContactModel();
        await contact.fetch(contactId);
        return contact;
    }

    /**
     * Replaces "SAINT" with "ST." in a given string.
     * @param {string} value - The string to process.
     * @returns {string} The processed string.
     */
    replaceSaintWithSt(value) {
        if (!value) return '';
        return value.replace(/SAINT/g, 'ST.');
    }

    /**
     * Converts the contact model to a JSON object.
     * @returns {Object} The JSON representation of the contact model.
     */
    toJSON() {
        return {
            contactId: this.contactId,
            primaryOrganizationId: this.primaryOrganizationId,
            firstName: this.firstName,
            lastName: this.lastName,
            address1: this.address1,
            address2: this.address2,
            city: this.city,
            stateProvince: this.stateProvince,
            countyId: this.countyId,
            otherCountyName: this.otherCountyName,
            postalCode: this.postalCode,
            phone: this.phone,
            phoneExtension: this.phoneExtension,
            fax: this.fax,
            email: this.email,
            isActive: this.isActive,
            isPortalUser: this.isPortalUser,
            isInternalUser: this.isInternalUser,
            isPendingApproval: this.isPendingApproval,
            title: this.title
        };
    }

    /**
     * Creates a ContactModel instance from a JSON object.
     * @param {Object} data - The JSON data.
     * @returns {ContactModel} A new ContactModel instance.
     */
    static fromJSON(data) {
        const instance = new ContactModel(data.isHceContact);
        instance.setData(data);
        return instance;
    }
}